import { useEffect, useRef } from 'react';
import { useFormValidation } from '../Components/Utils/useFormValidation';
import useFormRehydration from './useFormRehydration';

/**
 * Unified form setup hook that combines:
 * - Form validation via useFormValidation
 * - Form rehydration on error
 * - Payload synchronization
 * - Form error state management
 * - Debounced validation to reduce computation on rapid typing
 *
 * Eliminates the duplicated useEffect chains in form components
 *
 * @param {string} formName - Name of the form for validation rules
 * @param {object} formData - Current form data state
 * @param {Function} setFormData - State setter for form data
 * @param {Function} setPayLoad - Callback to update parent payload
 * @param {Function} setFormError - Callback to update parent form error state
 * @param {string|object} submittedData - Previously submitted data (for rehydration)
 * @param {string} errorCode - Error code from submission
 * @param {number} debounceMs - Debounce delay in ms for validation (default: 150)
 * @returns {object} Validation utilities from useFormValidation
 */
export const useFormSetup = ({
  formName,
  formData,
  setFormData,
  setPayLoad,
  setFormError,
  submittedData,
  errorCode,
  debounceMs = 150,
}) => {
  // Get validation utilities
  const validationResult = useFormValidation(formName);
  const { validateEntireForm, hasErrors } = validationResult;

  // Ref to track debounce timer
  const debounceTimerRef = useRef(null);

  // Rehydrate form data on error
  useFormRehydration(setFormData, submittedData, errorCode);

  // Sync payload immediately, debounce validation
  useEffect(() => {
    // Always sync payload immediately so parent has latest data
    if (setPayLoad) {
      setPayLoad(formData);
    }

    // Clear any pending validation
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Debounce validation to reduce computation during rapid typing
    debounceTimerRef.current = setTimeout(() => {
      validateEntireForm(formData);
    }, debounceMs);

    // Cleanup on unmount or before next effect
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, debounceMs]);

  // Sync form error state
  useEffect(() => {
    if (setFormError) {
      setFormError(hasErrors);
    }
  }, [hasErrors, setFormError]);

  return validationResult;
};

export default useFormSetup;
